import React, { memo, useMemo } from "react";

/**
 * RadialGauge
 * - Simple, dependency-free, semicircle gauge (0–max).
 * - Caps the fill at 100% of the arc when value > max.
 *
 * Props:
 *  value: number
 *  max?: number (default 100)
 *  size?: number (px, default 200)
 *  thickness?: number (stroke width, default 18)
 *  label?: string (center label under the value)
 *  format?: (v:number)=>string (value formatter; default `${v}`)
 */
function RadialGauge({
  value = 0,
  max = 100,
  size = 200,
  thickness = 18,
  format = (v) => `${v}`,
  showText = false,
}) {
  const clamped = Math.max(0, Math.min(value, max));
  const pct = max > 0 ? clamped / max : 0;

  // Geometry
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const startAngle = Math.PI; // 180°
  const endAngle = 0; //   0° (semicircle)

  const arcLen = Math.PI * r; // semicircle length
  const dash = arcLen * pct;
  const gap = arcLen - dash;

  // Paths use a circle trimmed to a semicircle with stroke-dasharray & offset.
  const circleProps = {
    cx,
    cy,
    r,
    fill: "none",
    strokeLinecap: "round",
    strokeWidth: thickness,
  };

  const textValue = useMemo(() => format(value), [value, format]);

  return (
    <div className="flex flex-col h-full items-center justify-center">
      <svg width={size} height={size / 2} viewBox={`0 0 ${size} ${size / 2}`}>
        {/* Track */}
        <circle
          {...circleProps}
          stroke="rgba(0,0,0,0.08)"
          strokeDasharray={`${arcLen} ${Math.PI * r}`}
          strokeDashoffset={arcLen}
        />
        {/* Fill */}
        <circle
          {...circleProps}
          stroke="currentColor"
          className="text-primary"
          strokeDasharray={`${dash} ${gap + Math.PI * r}`}
          strokeDashoffset={arcLen}
        />
      </svg>
      {showText && (
        <div className="mt-2 text-center leading-tight">
          <div className="text-2xl font-semibold">{textValue}</div>
        </div>
      )}
    </div>
  );
}

export default memo(RadialGauge);
